import { userService } from './userService';

// API endpoints
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Determine which API to use
const getApiBaseUrl = (): string => {
  // Check environment variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Check hostname
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return DEV_API_BASE; // localhost uses DEV
  }
  
  // Check if it's the dev S3 site
  if (hostname.includes('adly-app-dev')) {
    return DEV_API_BASE;
  }
  
  // Default to PROD
  return PROD_API_BASE;
};

const API_BASE_URL = getApiBaseUrl();

export interface SendEmailRequest {
  creator_id: string;
  to_email: string;
  to_name?: string;
  subject: string;
  html_content: string;
  plain_text: string;
  send_from_email: string;
  send_from_name?: string;
  table_id: string;
  row_id: string;
  column_id: string;
}

export interface SendEmailResponse {
  success: boolean;
  message_id?: string;
  status: 'sent' | 'failed';
  sent_at?: string;
  error?: string;
}

export interface GetOutreachStatusRequest {
  table_id: string;
  row_id: string;
  column_id: string;
}

export interface OutreachStatus {
  id: string;
  status: 'draft' | 'pending' | 'sent' | 'failed' | 'opened' | 'replied';
  to_email?: string;
  subject?: string;
  sent_at?: string;
  opened_at?: string;
  replied_at?: string;
  error?: string;
  message_id?: string;
}

export interface CheckOutreachSentRequest {
  creator_id: string;
  outreach_template: string;
  outreach_subject?: string;
}

export interface CheckOutreachSentResponse {
  success: boolean;
  sent: boolean;
  sent_at?: string;
  message_id?: string;
  confidence?: 'high' | 'medium' | 'low';
  reasoning?: string;
  reason?: string;
  sent_message?: {
    subject: string;
    content: string;
    sent_at: string;
  };
}

class OutreachService {
  /**
   * Send personalized email to creator via Outlook
   */
  async sendEmail(request: SendEmailRequest): Promise<SendEmailResponse> {
    const startTime = Date.now();
    
    try {
      const token = await userService.getAuthToken();
      
      console.log('');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📧 [OUTREACH-SEND] Sending email via Outlook...');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📋 Details:', {
        to: request.to_email,
        subject: request.subject,
        creator_id: request.creator_id,
        table_id: request.table_id,
        row_id: request.row_id,
        column_id: request.column_id
      });
      
      // Use Outlook new email endpoint
      const response = await fetch(`${API_BASE_URL}/outlook/new-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          to_recipients: [request.to_email],
          subject: request.subject,
          body: request.html_content,
          research_table_id: request.table_id,
          // Optional fields for tracking
          ticket_uuid: request.row_id,  // Use row_id as ticket reference
        })
      });

      const elapsed = Date.now() - startTime;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [OUTREACH-SEND] Failed:', {
          status: response.status,
          error: errorData,
          elapsed_ms: elapsed
        });
        
        // Check if it's a whitelist error
        if (response.status === 403 && errorData.blocked_recipients) {
          throw new Error(`Email blocked: Only allowed to send to ${errorData.allowed_recipients?.join(', ')}`);
        }
        
        throw new Error(errorData.error || `Email send failed: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('');
      console.log('✅ [OUTREACH-SEND] Email sent successfully!');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📊 Result:', {
        success: data.success,
        elapsed_ms: elapsed
      });
      console.log('═══════════════════════════════════════════════════════');
      console.log('');
      
      return {
        success: true,
        status: 'sent',
        sent_at: new Date().toISOString()
      };
      
    } catch (error) {
      const elapsed = Date.now() - startTime;
      console.log('');
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ [OUTREACH-SEND] Fatal error:', {
        error: error instanceof Error ? error.message : error,
        elapsed_ms: elapsed
      });
      console.error('═══════════════════════════════════════════════════════');
      console.log('');
      
      return {
        success: false,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get outreach status for a specific cell
   */
  async getOutreachStatus(request: GetOutreachStatusRequest): Promise<OutreachStatus | null> {
    try {
      const token = await userService.getAuthToken();
      
      const url = new URL(`${API_BASE_URL}/outreach-status`);
      url.searchParams.append('table_id', request.table_id);
      url.searchParams.append('row_id', request.row_id);
      url.searchParams.append('column_id', request.column_id);
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No outreach sent yet
        }
        throw new Error(`Failed to get outreach status: ${response.status}`);
      }

      const data = await response.json();
      return data.status ? data : null;
      
    } catch (error) {
      console.error('❌ [OUTREACH-SERVICE] Error getting status:', error);
      return null;
    }
  }

  /**
   * Check if outreach template has been sent to creator
   * Uses AI to match template against sent emails
   */
  async checkOutreachSent(request: CheckOutreachSentRequest): Promise<CheckOutreachSentResponse> {
    const startTime = Date.now();
    
    try {
      const token = await userService.getAuthToken();
      
      console.log('');
      console.log('═══════════════════════════════════════════════════════');
      console.log('🚀 [OUTREACH-TRACKER] Starting outreach check...');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📋 Request:', {
        creator_id: request.creator_id,
        template_length: request.outreach_template.length,
        has_subject: !!request.outreach_subject,
        endpoint: `${API_BASE_URL}/outreach-tracker`
      });
      
      const response = await fetch(`${API_BASE_URL}/outreach-tracker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      const elapsed = Date.now() - startTime;
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [OUTREACH-TRACKER] Request failed:', {
          status: response.status,
          error: errorData,
          elapsed_ms: elapsed
        });
        throw new Error(errorData.error || `Check failed: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('');
      console.log('✅ [OUTREACH-TRACKER] Check complete!');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📊 Result:', {
        sent: data.sent,
        sent_at: data.sent_at,
        confidence: data.confidence,
        has_proof: !!data.sent_message,
        elapsed_ms: elapsed
      });
      console.log('💬 Reasoning:', data.reasoning);
      if (data.sent_message) {
        console.log('📧 Sent Message:', {
          subject: data.sent_message.subject,
          content_preview: data.sent_message.content?.substring(0, 100) + '...'
        });
      }
      console.log('═══════════════════════════════════════════════════════');
      console.log('');
      
      return data;
      
    } catch (error) {
      const elapsed = Date.now() - startTime;
      console.log('');
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ [OUTREACH-TRACKER] Fatal error:', {
        error: error instanceof Error ? error.message : error,
        elapsed_ms: elapsed
      });
      console.error('═══════════════════════════════════════════════════════');
      console.log('');
      
      return {
        success: false,
        sent: false,
        reason: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Bulk send emails to multiple creators
   */
  async bulkSendEmails(requests: SendEmailRequest[]): Promise<SendEmailResponse[]> {
    console.log(`📧 [OUTREACH-SERVICE] Sending ${requests.length} emails...`);
    
    // Send in parallel with rate limiting
    const BATCH_SIZE = 5; // Send 5 at a time
    const results: SendEmailResponse[] = [];
    
    for (let i = 0; i < requests.length; i += BATCH_SIZE) {
      const batch = requests.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map(request => this.sendEmail(request))
      );
      results.push(...batchResults);
      
      // Small delay between batches to avoid rate limits
      if (i + BATCH_SIZE < requests.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ [OUTREACH-SERVICE] Sent ${successCount}/${requests.length} emails successfully`);
    
    return results;
  }
}

export const outreachService = new OutreachService();

